import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import API from '../../config/api';
import { collectQaDiagnosticSignals } from '../../utils/qaDiagnosticsSignals';

const GEO_DISCLAIMER = 'Approximate IP geolocation. It does not identify a physical residential street address.';

const STEPS = [
  'Verifying QA session',
  'Measuring public route',
  'Reading browser environment',
  'Checking WebRTC / DNS / IPv6 status',
  'Comparing observed results with selected target',
  'Saving admin report',
];

function gradeClass(grade) {
  if (grade === 'pass') return 'text-emerald-400';
  if (grade === 'fail') return 'text-red-400';
  return 'text-amber-400';
}

function Field({ label, value }) {
  return (
    <div className="bg-stone-950/70 border border-stone-800 rounded-lg p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</p>
      <p className="text-sm text-white break-words">{value || 'Unavailable'}</p>
    </div>
  );
}

export default function QaDiagnosticsPage() {
  const { runId } = useParams();
  const [searchParams] = useSearchParams();
  const proof = searchParams.get('p') || searchParams.get('token') || '';
  const [status, setStatus] = useState('loading');
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [bootstrap, setBootstrap] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!runId || !proof) {
        setError('This diagnostics session is missing its run proof.');
        setStatus('error');
        return;
      }
      try {
        setStep(0);
        const bootstrapResponse = await fetch(`${API}/qa-diagnostics/runs/${encodeURIComponent(runId)}?p=${encodeURIComponent(proof)}`);
        const bootstrapPayload = await bootstrapResponse.json().catch(() => ({}));
        if (!bootstrapResponse.ok) {
          throw new Error(bootstrapPayload.detail || 'Unable to open QA diagnostics.');
        }
        if (cancelled) return;
        setBootstrap(bootstrapPayload);
        const submitToken = bootstrapPayload.submitToken;
        if (!submitToken) {
          if (bootstrapPayload.completed) {
            setStatus('saved');
            return;
          }
          throw new Error('This diagnostics session is missing its run proof.');
        }
        setStatus('measuring');
        setStep(1);
        await fetch(
          `${API}/qa-diagnostics/runs/${encodeURIComponent(runId)}/route?submitToken=${encodeURIComponent(submitToken)}`,
        ).catch(() => null);
        if (cancelled) return;
        setStep(2);
        const signals = await collectQaDiagnosticSignals();
        if (cancelled) return;
        setStep(3);
        setStep(4);
        setStep(5);
        const submitResponse = await fetch(`${API}/qa-diagnostics/runs/${encodeURIComponent(runId)}/report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            submitToken,
            browserSignals: signals,
          }),
        });
        const submitPayload = await submitResponse.json().catch(() => ({}));
        if (!submitResponse.ok) {
          throw new Error(submitPayload.detail || 'Unable to save the QA diagnostics report.');
        }
        if (cancelled) return;
        setReport(submitPayload);
        setStatus('saved');
      } catch (err) {
        if (cancelled) return;
        setError(String(err?.message || err || 'Unable to run QA diagnostics.'));
        setStatus('error');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [runId, proof]);

  const geo = report?.approximateGeo || {};
  const classification = report?.proxyClassification || {};
  const signals = report?.browserEnvironment || report?.browserSignals || {};
  const leaks = report?.leakResults || {};
  const consistency = report?.consistency || {};

  return (
    <div className="min-h-screen bg-stone-950 text-slate-200" data-testid="qa-diagnostics-page">
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-2">FundElite Authorized QA Proxy</p>
          <h1 className="text-3xl font-semibold text-white">QA Diagnostics</h1>
          <p className="text-slate-400 mt-2">
            Controlled FundElite measurement of the proxy exit and browser signals for this session.
          </p>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="qa-diagnostics-geo-disclaimer">
          <p className="text-sm text-amber-100">{bootstrap?.geoDisclaimer || GEO_DISCLAIMER}</p>
        </div>

        {status === 'loading' || status === 'measuring' ? (
          <div data-testid="qa-diagnostics-status">
            <p className="text-slate-400 mb-3">{STEPS[step] || 'Opening diagnostics session…'}</p>
            <ol className="space-y-1 text-sm text-slate-500">
              {STEPS.map((item, index) => (
                <li key={item} className={index === step ? 'text-amber-200' : ''}>{index + 1}. {item}</li>
              ))}
            </ol>
          </div>
        ) : null}

        {error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="qa-diagnostics-error">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        ) : null}

        {bootstrap ? (
          <p className="text-sm text-slate-400" data-testid="qa-diagnostics-target">
            DataImpulse target: {bootstrap.targetLocation || '—'}
          </p>
        ) : null}

        {report ? (
          <div className="space-y-6" data-testid="qa-diagnostics-report">
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-400">Result</p>
              <p className={`text-lg font-semibold ${gradeClass(report.grade)}`} data-testid="qa-diagnostics-grade">
                {report.displayResult || report.grade || 'Complete'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Public exit IP" value={report.exitIp} />
              <Field label="Estimated country / region (approximate)" value={[geo.country, geo.region].filter(Boolean).join(' / ')} />
              <Field label="Estimated city (approximate)" value={geo.city} />
              <Field label="Observed postal code (approximate)" value={geo.postal} />
              <Field label="ASN / network" value={[geo.asn, geo.org].filter(Boolean).join(' · ')} />
              <Field label="Proxy classification" value={classification.label} />
              <Field label="Network type" value={classification.networkType} />
              <Field label="Timezone" value={signals.timezone} />
              <Field label="Locale" value={Array.isArray(signals.languages) ? [signals.locale, ...(signals.languages || [])].filter(Boolean).join(', ') : signals.locale} />
              <Field
                label="Screen"
                value={signals.screenWidth ? `${signals.screenWidth}×${signals.screenHeight}` : (signals.screen ? `${signals.screen.width}×${signals.screen.height}` : '')}
              />
              <Field
                label="Viewport"
                value={signals.innerViewportWidth ? `${signals.innerViewportWidth}×${signals.innerViewportHeight}` : (signals.viewport ? `${signals.viewport.width}×${signals.viewport.height}` : '')}
              />
              <Field label="Device pixel ratio" value={signals.devicePixelRatio != null ? String(signals.devicePixelRatio) : ''} />
              <Field label="WebRTC" value={leaks.webrtc} />
              <Field label="DNS" value={leaks.dns} />
              <Field label="IPv6" value={leaks.ipv6} />
              <Field
                label="Location consistency"
                value={consistency.status || (consistency.countryMatches ? 'Observed country matches the selected DataImpulse target' : 'Observed country does not match the selected DataImpulse target')}
              />
            </div>

            <p className="text-xs text-slate-500" data-testid="qa-diagnostics-classification-detail">
              {classification.detail || GEO_DISCLAIMER}
            </p>
            <p className="text-xs text-slate-500">{leaks.note}</p>

            <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
              <p className="text-sm font-medium text-white mb-2">Findings</p>
              <ul className="space-y-2">
                {(report.findings || []).map((item) => (
                  <li key={`${item.code}-${item.message}`} className="text-sm text-slate-300">
                    <span className={`uppercase text-xs mr-2 ${gradeClass(item.severity)}`}>{item.severity}</span>
                    {item.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
